import { Box, Group } from '@mantine/core';
import QuestionMarkTooltip from 'components/QuestionMarkTooltip';
import P2Regular from 'components/typography/P2Regular';
import P3Medium from 'components/typography/P3Medium';
import P3Regular from 'components/typography/P3Regular';

export interface MeasurementContext {
  tableName: string;
  rowNumber: number;
  values: { key: string; value: string }[];
}

/**
 * Formats measurement context for compact display.
 */
export function getMeasurementContextText(measurementContext: MeasurementContext) {
  return `Riadok č.${measurementContext.rowNumber} V tabuľke ${measurementContext.tableName}`;
}

/**
 * Tooltip content for measurement row values.
 */
function MeasurementValuesTable({ values }: { values: MeasurementContext['values'] }) {
  const visibleValues = values.filter(({ key }) => key !== '_id');

  if (visibleValues.length === 0) {
    return <P3Regular color="gray.0">Žiadne ďalšie hodnoty</P3Regular>;
  }

  return (
    <Box component="table" sx={{ borderCollapse: 'collapse', maxWidth: 420 }}>
      <tbody>
        {visibleValues.map(({ key, value }, index) => (
          <tr key={`${key}-${index}`}>
            <Box
              component="td"
              sx={(theme) => ({
                borderBottom: index === visibleValues.length - 1 ? 'none' : `1px solid ${theme.colors.gray[3]}`,
                padding: '2px 10px 2px 0',
                verticalAlign: 'top',
              })}
            >
              <P3Medium color="gray.0">{key}</P3Medium>
            </Box>
            <Box
              component="td"
              sx={(theme) => ({
                borderBottom: index === visibleValues.length - 1 ? 'none' : `1px solid ${theme.colors.gray[3]}`,
                padding: '2px 0',
                verticalAlign: 'top',
              })}
            >
              <P3Regular color="gray.0">{value}</P3Regular>
            </Box>
          </tr>
        ))}
      </tbody>
    </Box>
  );
}

/**
 * Displays measurement row context with a tooltip containing row values.
 */
export default function MeasurementContextDisplay({
  measurementContext,
  text,
}: {
  measurementContext?: MeasurementContext;
  text?: string;
}) {
  if (!measurementContext) {
    return null;
  }

  return (
    <Group spacing={6} noWrap>
      <P2Regular color="gray.8" lineClamp={2}>
        {text ?? getMeasurementContextText(measurementContext)}
      </P2Regular>
      <QuestionMarkTooltip
        label={<MeasurementValuesTable values={measurementContext.values} />}
        multiline
        openDelay={100}
        position="top"
        withinPortal
      />
    </Group>
  );
}
